import { type UpsertLeaveWorkPatternRequest } from '@gb-ticket/shared-types';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { leavePanelClassName } from '@/products/leave/features/leave-panel';
import { useLeavePeople } from '@/products/leave/features/use-leave';
import {
  useCreateLeaveAdjustmentAdmin,
  useCreateLeaveOvertimeAdmin,
  useSetLeavePersonnelNumber,
  useUpsertLeaveWorkPatternAdmin,
} from '@/products/leave/features/use-leave-admin';

export function LeaveAdminBalancesPage() {
  const copy = useVoiceCopy();
  usePageTitle('Salden');
  const [query, setQuery] = useState('');
  const people = useLeavePeople(query, { includeSelf: true, minLength: 0 });
  const [userId, setUserId] = useState<string | null>(null);
  const pattern = useUpsertLeaveWorkPatternAdmin();
  const adjustment = useCreateLeaveAdjustmentAdmin();
  const overtime = useCreateLeaveOvertimeAdmin();
  const personnel = useSetLeavePersonnelNumber();

  const [patternDraft, setPatternDraft] = useState({
    effectiveFrom: `${new Date().getFullYear()}-01-01`,
    stateCode: 'NW',
    mon: '1',
    tue: '1',
    wed: '1',
    thu: '1',
    fri: '1',
    sat: '0',
    sun: '0',
    hoursPerFullDay: '',
  });
  const [adjustmentDraft, setAdjustmentDraft] = useState({
    year: String(new Date().getFullYear()),
    days: '',
    reason: '',
  });
  const [overtimeDraft, setOvertimeDraft] = useState({
    occurredOn: new Date().toISOString().slice(0, 10),
    hours: '',
    note: '',
  });
  const [errors, setErrors] = useState<string | null>(null);

  async function onPatternSubmit(event: FormEvent) {
    event.preventDefault();
    if (!userId) {
      setErrors('Bitte zuerst eine Person auswählen.');
      return;
    }
    setErrors(null);
    try {
      const body: UpsertLeaveWorkPatternRequest = {
        effectiveFrom: patternDraft.effectiveFrom,
        stateCode: patternDraft.stateCode as UpsertLeaveWorkPatternRequest['stateCode'],
        mon: Number(patternDraft.mon) as UpsertLeaveWorkPatternRequest['mon'],
        tue: Number(patternDraft.tue) as UpsertLeaveWorkPatternRequest['tue'],
        wed: Number(patternDraft.wed) as UpsertLeaveWorkPatternRequest['wed'],
        thu: Number(patternDraft.thu) as UpsertLeaveWorkPatternRequest['thu'],
        fri: Number(patternDraft.fri) as UpsertLeaveWorkPatternRequest['fri'],
        sat: Number(patternDraft.sat) as UpsertLeaveWorkPatternRequest['sat'],
        sun: Number(patternDraft.sun) as UpsertLeaveWorkPatternRequest['sun'],
        hoursPerFullDay: patternDraft.hoursPerFullDay ? Number(patternDraft.hoursPerFullDay) : null,
      };
      await pattern.mutateAsync({ userId, body });
      toast.success('Arbeitswoche gespeichert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Arbeitswoche konnte nicht gespeichert werden.',
      );
    }
  }

  async function onAdjustmentSubmit(event: FormEvent) {
    event.preventDefault();
    if (!userId) {
      setErrors('Bitte zuerst eine Person auswählen.');
      return;
    }
    if (!adjustmentDraft.reason.trim()) {
      setErrors('Bitte einen Grund für die Korrektur angeben.');
      return;
    }
    setErrors(null);
    try {
      await adjustment.mutateAsync({
        userId,
        year: Number(adjustmentDraft.year),
        days: Number(adjustmentDraft.days),
        reason: adjustmentDraft.reason.trim(),
      });
      setAdjustmentDraft((current) => ({ ...current, days: '', reason: '' }));
      toast.success('Korrektur gespeichert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Korrektur konnte nicht gespeichert werden.',
      );
    }
  }

  async function onOvertimeSubmit(event: FormEvent) {
    event.preventDefault();
    if (!userId) {
      setErrors('Bitte zuerst eine Person auswählen.');
      return;
    }
    setErrors(null);
    try {
      await overtime.mutateAsync({
        userId,
        occurredOn: overtimeDraft.occurredOn,
        hours: Number(overtimeDraft.hours),
        note: overtimeDraft.note.trim() || null,
      });
      setOvertimeDraft((current) => ({ ...current, hours: '', note: '' }));
      toast.success('Überstunden gespeichert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Überstunden konnten nicht gespeichert werden.',
      );
    }
  }

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title="Salden"
        description="Arbeitswoche setzen, Urlaubssaldo korrigieren und Überstunden buchen."
      />
      <section className={`${leavePanelClassName} space-y-3`}>
        <h2 className="text-lg font-semibold">Person</h2>
        <Label htmlFor="balance-person">Suche</Label>
        <Input
          id="balance-person"
          className="mt-2"
          value={query}
          placeholder="Name oder E-Mail, z. B. Emma oder user@"
          onChange={(event) => setQuery(event.target.value)}
        />
        {people.isPending ? (
          <p className="text-sm text-muted-foreground">Personen werden geladen …</p>
        ) : null}
        {people.isError ? (
          <p className="text-sm text-destructive">Die Personenliste konnte nicht geladen werden.</p>
        ) : null}
        {people.data?.items.length ? (
          <ul className="max-h-64 space-y-1 overflow-auto rounded-md border p-2">
            {people.data.items.map((person) => (
              <li key={person.id}>
                <button
                  type="button"
                  className={`w-full rounded px-2 py-1 text-left text-sm hover:bg-muted ${
                    userId === person.id ? 'bg-muted font-medium' : ''
                  }`}
                  onClick={() => setUserId(person.id)}
                >
                  <span className="block">{person.displayName}</span>
                  {person.email ? (
                    <span className="block text-xs text-muted-foreground">{person.email}</span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        ) : people.data && !people.isPending ? (
          <p className="text-sm text-muted-foreground">Keine Person gefunden.</p>
        ) : null}
        {userId ? (
          <p className="text-sm">
            Gewählt:{' '}
            {people.data?.items.find((person) => person.id === userId)?.displayName ?? userId}
          </p>
        ) : null}
        {errors ? <p className="text-sm text-destructive">{errors}</p> : null}
        {userId ? (
          <PersonnelNumberForm
            key={userId}
            userId={userId}
            initial={
              people.data?.items.find((person) => person.id === userId)?.personnelNumber ?? ''
            }
            pending={personnel.isPending}
            onSave={async (personnelNumber) => {
              await personnel.mutateAsync({ userId, personnelNumber });
              toast.success('Personalnummer gespeichert.');
            }}
          />
        ) : null}
      </section>
      <form
        className={`${leavePanelClassName} space-y-4`}
        onSubmit={(event) => void onPatternSubmit(event)}
      >
        <h2 className="text-lg font-semibold">Arbeitswoche</h2>
        <p className="text-sm text-muted-foreground">{copy('leave.balance.week-hint')}</p>
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <Label htmlFor="pattern-from">Gültig ab</Label>
            <Input
              id="pattern-from"
              type="date"
              className="mt-2"
              value={patternDraft.effectiveFrom}
              onChange={(event) =>
                setPatternDraft((current) => ({ ...current, effectiveFrom: event.target.value }))
              }
            />
          </div>
          <div>
            <Label htmlFor="pattern-state">Bundesland</Label>
            <Input
              id="pattern-state"
              className="mt-2"
              value={patternDraft.stateCode}
              onChange={(event) =>
                setPatternDraft((current) => ({ ...current, stateCode: event.target.value }))
              }
            />
          </div>
          <div>
            <Label htmlFor="pattern-hours">Stunden pro Tag</Label>
            <Input
              id="pattern-hours"
              className="mt-2"
              value={patternDraft.hoursPerFullDay}
              onChange={(event) =>
                setPatternDraft((current) => ({ ...current, hoursPerFullDay: event.target.value }))
              }
            />
          </div>
        </div>
        <div className="grid grid-cols-7 gap-2">
          {(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const).map((day) => (
            <div key={day}>
              <Label htmlFor={`pattern-${day}`}>{day.toUpperCase()}</Label>
              <Input
                id={`pattern-${day}`}
                className="mt-2"
                value={patternDraft[day]}
                onChange={(event) =>
                  setPatternDraft((current) => ({ ...current, [day]: event.target.value }))
                }
              />
            </div>
          ))}
        </div>
        <Button type="submit" disabled={pattern.isPending}>
          Arbeitswoche speichern
        </Button>
      </form>
      <form
        className={`${leavePanelClassName} space-y-4`}
        onSubmit={(event) => void onAdjustmentSubmit(event)}
      >
        <h2 className="text-lg font-semibold">Saldo korrigieren</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <Label htmlFor="adjustment-year">Jahr</Label>
            <Input
              id="adjustment-year"
              className="mt-2"
              value={adjustmentDraft.year}
              onChange={(event) =>
                setAdjustmentDraft((current) => ({ ...current, year: event.target.value }))
              }
            />
          </div>
          <div>
            <Label htmlFor="adjustment-days">Tage (+/−)</Label>
            <Input
              id="adjustment-days"
              className="mt-2"
              value={adjustmentDraft.days}
              onChange={(event) =>
                setAdjustmentDraft((current) => ({ ...current, days: event.target.value }))
              }
            />
          </div>
          <div className="sm:col-span-3">
            <Label htmlFor="adjustment-reason">Grund (Pflicht)</Label>
            <Input
              id="adjustment-reason"
              className="mt-2"
              value={adjustmentDraft.reason}
              onChange={(event) =>
                setAdjustmentDraft((current) => ({ ...current, reason: event.target.value }))
              }
            />
          </div>
        </div>
        <Button type="submit" disabled={adjustment.isPending}>
          Korrektur speichern
        </Button>
      </form>
      <form
        className={`${leavePanelClassName} space-y-4`}
        onSubmit={(event) => void onOvertimeSubmit(event)}
      >
        <h2 className="text-lg font-semibold">Überstunden buchen</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <Label htmlFor="overtime-date">Datum</Label>
            <Input
              id="overtime-date"
              type="date"
              className="mt-2"
              value={overtimeDraft.occurredOn}
              onChange={(event) =>
                setOvertimeDraft((current) => ({ ...current, occurredOn: event.target.value }))
              }
            />
          </div>
          <div>
            <Label htmlFor="overtime-hours">Stunden</Label>
            <Input
              id="overtime-hours"
              className="mt-2"
              value={overtimeDraft.hours}
              onChange={(event) =>
                setOvertimeDraft((current) => ({ ...current, hours: event.target.value }))
              }
            />
          </div>
          <div className="sm:col-span-3">
            <Label htmlFor="overtime-note">Notiz</Label>
            <Input
              id="overtime-note"
              className="mt-2"
              value={overtimeDraft.note}
              onChange={(event) =>
                setOvertimeDraft((current) => ({ ...current, note: event.target.value }))
              }
            />
          </div>
        </div>
        <Button type="submit" disabled={overtime.isPending}>
          Stunden speichern
        </Button>
      </form>
    </div>
  );
}

function PersonnelNumberForm({
  userId,
  initial,
  pending,
  onSave,
}: {
  userId: string;
  initial: string;
  pending: boolean;
  onSave: (personnelNumber: string | null) => Promise<void>;
}) {
  const [value, setValue] = useState(initial);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    try {
      await onSave(value.trim() === '' ? null : value.trim());
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Personalnummer konnte nicht gespeichert werden.',
      );
    }
  }

  return (
    <form className="space-y-2 border-t pt-3" onSubmit={(event) => void onSubmit(event)}>
      <Label htmlFor={`personnel-${userId}`}>Personalnummer</Label>
      <Input
        id={`personnel-${userId}`}
        className="mt-2 max-w-xs"
        value={value}
        maxLength={20}
        onChange={(event) => setValue(event.target.value)}
      />
      <p className="text-xs text-muted-foreground">
        Dieselbe Nummer wie in der Benutzerverwaltung. Der Verzeichnis-Sync schreibt sie nicht.
      </p>
      <Button type="submit" variant="outline" disabled={pending}>
        Personalnummer speichern
      </Button>
    </form>
  );
}
